import { flatten } from 'utils/array';
import Fetch from 'utils/fetch';
import { settings } from 'settings';

import Swal from 'sweetalert2';
import {
  BundleJson,
  Instruction,
  InstructionType,
  ReorderMode,
  TravelMode,
} from '../../../types/bundleTypes';
import {
  Order,
  OrderListOrder,
  OrderListOrderClazz,
  OrderMonitor,
  Region,
} from '../../../types/orderTypes';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../../utils/pusher';
import {
  OrderInstruction,
  toOrderInstructions,
} from '../../../components/maps/BundleMap';
import { kelly_colors, MAX_CONTRAST_COLORS } from '../../../const/color';
import {
  fetchOrdersBundles,
  fetchOrdersForMonitor,
} from '../../../fetch/orderFetch';
import { PlannerOrder } from '../../planner/utils/usePlannerData';
import { markMultipleOrdersAsReady } from '../../orders/OrdersUtil';

export const WINDOW_COLORS = [
  'rgba(243,195,0, 1)',
  'rgba(135,86,146, 1)',
  'rgba(190,0,50, 1)',
  'rgba(161,202,241, 1)',
  'rgba(230,143,172, 1)',
  'rgba(141,182,0, 1)',
  'rgba(243,132,0, 1)',
  'rgba(179,68,108, 1)',
  'rgba(226,88,34, 1)',
];

export function getBundleColor(bundleDraftId: string): string {
  const id = Number(bundleDraftId);
  const idx = Number.isNaN(id) ? 0 : id % MAX_CONTRAST_COLORS.length;
  return MAX_CONTRAST_COLORS[idx];
}

function padZero(str: string, len = 2): string {
  len = len || 2;
  const zeros = [len].join('0');
  return (zeros + str).slice(-len);
}

export function invertColor(hex: string, bw: boolean): string {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error(`Invalid HEX color: ${hex}`);
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (bw) {
    // http://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
  }
  // invert color components
  const red = (255 - r).toString(16);
  const green = (255 - g).toString(16);
  const blue = (255 - b).toString(16);
  // pad each with zeros and return
  return `#${padZero(red)}${padZero(green)}${padZero(blue)}`;
}

export function getBundleFontColor(bundleDraftId: string): string {
  const id = Number(bundleDraftId);
  const idx: number = Number.isNaN(id) ? 0 : id % kelly_colors.length;
  return invertColor(kelly_colors[idx], true);
}

export function convertOrdersToDraftBundleFormat(
  orders: (Order | OrderListOrder | PlannerOrder)[]
): Instruction[] {
  return flatten(
    orders.map(order => {
      return [
        {
          orderId: order.id,
          type: InstructionType.Pickup,
          completed: false,
        },
        {
          orderId: order.id,
          type: InstructionType.Delivery,
          completed: false,
        },
      ];
    })
  );
}

export async function deleteDraftBundle(
  bundleDraftId: string
): Promise<boolean> {
  const response = await Fetch.delete({
    url: `${settings.newApiUrl}/bundle/${bundleDraftId}`,
  });

  if (response.ok) {
    return true;
  } else {
    throw Error(await response.text());
  }
}

export async function calculateEtaWithAlgorithm(
  bundleId: string,
  travelMode: TravelMode,
  timePerStop: number,
  successHandler: (completionInfo: any) => void,
  errorHandler: (response: Response) => void
) {
  try {
    let travelModeStr: string;
    if (travelMode === TravelMode.CAR) {
      travelModeStr = 'car';
    } else {
      travelModeStr = travelMode === TravelMode.BIKE ? 'bike' : '';
    }
    const response: Response = await Fetch.put({
      url: `${settings.newApiUrl}/bundle/${bundleId}/generate-eta?travelMode=${travelModeStr}&timePerStop=${timePerStop}`,
    });

    if (response.ok) {
      const sessionInfo = await response.json();
      const channel = sessionInfo.sessionTopic;

      subscribeToChannel(channel, PusherEvent.COMPLETED, completionInfo => {
        successHandler(completionInfo);
        unsubscribeFromChannel(channel);
      });
      subscribeToChannel(channel, PusherEvent.ERROR, error => {
        errorHandler(error);
        unsubscribeFromChannel(channel);
      });
    } else {
      errorHandler(response);
    }
  } catch (e) {
    errorHandler(e as Response);
  }
}

export async function reorderBundleWithAlgorithm(
  mode: ReorderMode,
  bundleId: string,
  onSuccess: () => void,
  onError: (error) => void
) {
  const response: Response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleId}/reorder/auto?mode=${mode}`,
  });

  if (response.ok) {
    const sessionInfo = await response.json();
    const channel = sessionInfo.sessionTopic;

    subscribeToChannel(channel, PusherEvent.COMPLETED, completionInfo => {
      unsubscribeFromChannel(channel);
      onSuccess();
    });
    subscribeToChannel(channel, PusherEvent.ERROR, error => {
      unsubscribeFromChannel(channel);
      onError(error);
    });
  } else {
    console.error('Initiating auto reorder failed', await response.text());
    onError(`Initiating auto reorder failed`);
  }
}

export async function splitBundleAndCreateNew(
  listOfOrders: string[],
  region: Region,
  bundleId: string
): Promise<BundleJson> {
  const response: Response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/split-bundle/${bundleId}/${region}`,
    body: {
      listOfOrders,
    },
  });
  if (!response.ok) {
    throw new Error(`Could not create bundle. Status: ${response.status}`);
  }
  return response.json();
}

export async function createBundleDraftIsolated(
  orders: (Order | OrderListOrder | PlannerOrder)[],
  region: Region
): Promise<BundleJson> {
  const instructions: Instruction[] = convertOrdersToDraftBundleFormat(orders);

  const response: Response = await Fetch.post({
    url: `${settings.newApiUrl}/bundle?region=${region}`,
    body: instructions,
  });
  if (!response.ok) {
    throw new Error(`Could not create bundle. Status: ${response.status}`);
  }
  return response.json();
}

export async function createBundleDraft(
  isLoading: boolean,
  orders: Order[],
  region: Region,
  retrying = false
) {
  if (isLoading) {
    return;
  }

  const instructions: Instruction[] = convertOrdersToDraftBundleFormat(orders);

  const response: Response = await Fetch.post({
    url: `${settings.newApiUrl}/bundle?region=${region}`,

    body: instructions,
  });

  if (response.status === 422) {
    const errors = await response.json();
    const message =
      errors && errors.length > 0 ? errors.map(e => e.message).join('\n') : '';

    if (retrying) {
      Swal.fire('Bundle could not be created', message, 'error');
      return;
    }

    // check if orders are already in a bundle
    const ordersToAdd = instructions
      .map(instr => instr.orderId)
      .filter((orderId, idx, array) => array.indexOf(orderId) === idx);
    const ordersBundles = await fetchOrdersBundles(ordersToAdd);
    const ordersAlreadyInBundle = ordersBundles.filter(ob => !!ob.bundle);

    if (ordersAlreadyInBundle.length === 0) {
      Swal.fire('Bundle could not be created', message, 'error');
      return;
    }

    Swal.fire({
      html: moveOrdersMessage(ordersAlreadyInBundle, null),
      title: 'Move orders to new bundle?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Move orders',
      cancelButtonText: 'Cancel',
    })
      .then(async result => {
        if (result.value) {
          await removeOrdersFromBundle(
            ordersAlreadyInBundle.map(ob => ob.orderId)
          );
          // retry adding the orders to a new bundle
          createBundleDraft(isLoading, orders, region, true);
        }
      })
      .catch(error => console.error(error.message));
  } else {
    alert('Something went wrong');
  }
}

export async function removeInstructionsFromBundle(
  bundle: BundleJson,
  orderIds: string[]
): Promise<void> {
  const updatedInstructions = bundle.instructions.filter(
    instruction => !orderIds.includes(instruction.orderId)
  );

  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundle.id}`,
    body: updatedInstructions,
  });

  if (!response.ok) {
    const errors = await response.json();
    const message =
      errors && errors.length > 0
        ? errors.map(e => e.message).join('.\r\n')
        : '';
    throw new Error(`Bundle could not be updated: ${message}`);
  }
  return;
}

export async function moveOrdersToSelectedBundle(
  bundleIdFrom: string,
  bundleIdTo: string,
  listOfOrders: string[]
): Promise<Response> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleIdFrom}/move-to-bundle/${bundleIdTo}`,
    body: {
      listOfOrders,
    },
  });
  if (!response.ok) {
    const errors = await response.json();
    const message =
      errors && errors.length > 0
        ? errors.map(e => e.message).join('.\r\n')
        : '';
    throw new Error(`Bundle could not be updated: ${message}`);
  }
  return response;
}

export async function publishDraft(bundleDraftId: string): Promise<BundleJson> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleDraftId}/publish`,
  });
  return response.ok ? await response.json() : null;
}

export const publishMultipleDraft = async (bundleIds: string[]) => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/publish`,
    body: {
      bundleIds,
    },
  });
  if (!response.ok) {
    const errors = await response.json();
    throw new Error(`Bundle could not be published : ${errors}`);
  }
  return response;
};

export async function reorderDraftNew(
  bundleDraftId: string,
  instructions: Instruction[]
): Promise<BundleJson> {
  const response: Response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleDraftId}/reorder`,

    body: instructions,
  });

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const respJson = await response.json();
    if (respJson.length > 0) {
      const errorMessage = respJson.map(error => error.message).join('. ');
      throw new Error(errorMessage);
    }
  }
}

export function pickupOnTop(
  orderInstructions: OrderInstruction[]
): OrderInstruction[] {
  const pickupMap = new Map(
    orderInstructions
      .filter(
        orderInstruction =>
          orderInstruction.instruction.type === InstructionType.Pickup
      )
      .map(instruction => [instruction.order.id, instruction])
  );
  const deliveries = orderInstructions.filter(
    orderInstruction =>
      orderInstruction.instruction.type === InstructionType.Delivery
  );
  const pickups = deliveries
    .map(delivery => pickupMap.get(delivery.order.id))
    .reverse();
  return pickups.concat(deliveries);
}

export const pickupOnTopWithGrouping = async (bundle: BundleJson) => {
  let fetchedOrders: OrderMonitor[];
  try {
    fetchedOrders = await fetchOrdersForMonitor(
      bundle.instructions.map(instruction => instruction.orderId)
    );
  } catch {
    fetchedOrders = [];
  }
  /* This `toOrderInstructions` will be used for grouping the bundle with the same address  */
  const orderInstruction = toOrderInstructions(bundle, fetchedOrders);

  /* Automatically do PickupTop after creation */
  await reorderDraftNew(
    bundle.id,
    pickupOnTop(orderInstruction).map(
      orderInstruction => orderInstruction.instruction
    )
  );
};

export function reverseDeliveries(
  orderInstructions: OrderInstruction[]
): OrderInstruction[] {
  const pickupMap = new Map(
    orderInstructions
      .filter(
        orderInstruction =>
          orderInstruction.instruction.type === InstructionType.Pickup
      )
      .map(instruction => [instruction.order.id, instruction])
  );
  const deliveries = orderInstructions
    .filter(
      orderInstruction =>
        orderInstruction.instruction.type === InstructionType.Delivery
    )
    .reverse();
  const pickups = deliveries
    .map(delivery => pickupMap.get(delivery.order.id))
    .reverse();
  return pickups.concat(deliveries);
}

export interface OrderBundle {
  orderId: string;
  bundle?: BundleJson;
}

export async function removeOrdersFromBundle(orderIds: string[]) {
  return await markMultipleOrdersAsReady(orderIds, false);
}

export function moveOrdersMessage(
  ordersAlreadyInBundle: OrderBundle[],
  targetBundleId: string | null
): string {
  const question = targetBundleId
    ? `Would you like to move them to bundle
          &nbsp;<span class="planner-map__move-bundle_icon" style="background-color: ${getBundleColor(
            targetBundleId
          )}"></span>
          <span style="font-weight: bold">${targetBundleId}</span>?
      `
    : 'Would you like to move them to a new bundle?';

  return `<div>
          The selected orders are already in an existing bundle:
          <br/><br/>
          ${ordersAlreadyInBundle
            .map(
              ob =>
                `Order <span style="font-weight: bold">${
                  ob.orderId
                }</span> is in bundle &nbsp;<span class="planner-map__move-bundle_icon" style="background-color: ${getBundleColor(
                  ob.bundle.id
                )}"></span>&nbsp;${ob.bundle.id}<br />`
            )
            .join('')}
          <br/><br/>
         ${question}
        </div>
      `;
}

export async function addSelectedOrdersToBundleWrapper(
  bundleId: string,
  selectedOrders: (OrderListOrderClazz | PlannerOrder)[],
  reFetch: () => void,
  clearSelectedOrders: () => void
): Promise<void> {
  addSelectedOrdersToBundle(bundleId, selectedOrders)
    .then(() => {
      reFetch();
      clearSelectedOrders();
    })
    .catch((error: Error) => {
      Swal.fire('Could not add order to bundle', error.message, 'error');
    });
}

export async function getOrdersAlreadyInBundle(
  orders: OrderListOrderClazz[]
): Promise<OrderBundle[]> {
  const orderBundleList = await fetchOrdersBundles(
    orders.map(order => order.id)
  );
  return orderBundleList.filter(ob => !!ob.bundle);
}

export async function addSelectedOrdersToBundle(
  bundleId: string,
  orders: (OrderListOrder | PlannerOrder)[]
) {
  const additionalSteps = convertOrdersToDraftBundleFormat(orders);
  const bundleResponse: Response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle/${bundleId}`,
  });
  const bundle: BundleJson = await bundleResponse.json();

  const result = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleId}`,

    body: bundle.instructions.concat(additionalSteps),
  });

  if (!result.ok) {
    const respJson = await result.json();
    if (respJson.length > 0) {
      const errorMessage = respJson.map(error => error.message).join('. ');
      throw new Error(errorMessage);
    }
    throw new Error(await result.text());
  }
}

export async function addSelectedOrdersToBundleDraft(
  isLoading: boolean,
  bundleDraftId: string,
  bundleDrafts: BundleJson[],
  newInstructions: Instruction[],
  region: Region,
  retrying = false
) {
  if (isLoading) {
    return;
  }

  const bundleDraft = bundleDrafts.find(bundle => bundle.id === bundleDraftId);
  const currentInstructions = bundleDraft.instructions;

  // skip new instructions already in the bundle
  const instructionsToAdd = newInstructions.filter(instr => {
    return !currentInstructions.some(currentInstr => {
      return (
        currentInstr.orderId === instr.orderId &&
        currentInstr.type === instr.type
      );
    });
  });

  if (instructionsToAdd.length === 0) {
    return; // nothing new to add
  }

  const response: Response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleDraftId}`,

    body: currentInstructions.concat(instructionsToAdd),
  });

  if (response.status === 422) {
    const errors = await response.json();
    const message =
      errors && errors.length > 0
        ? errors.map(e => e.message).join('.\r\n')
        : '';
    if (retrying) {
      Swal.fire('Bundle could not be updated', message, 'error');
      return;
    }

    // check if orders are already in a different bundle
    const ordersToAdd = instructionsToAdd
      .map(instr => instr.orderId)
      .filter((orderId, idx, array) => array.indexOf(orderId) === idx);
    const ordersBundles = await fetchOrdersBundles(ordersToAdd);
    const ordersAlreadyInBundle = ordersBundles.filter(
      ob => ob.bundle && ob.bundle.id !== bundleDraftId
    );

    if (ordersAlreadyInBundle.length === 0) {
      Swal.fire('Bundle could not be updated', message, 'error');
      return;
    }

    Swal.fire({
      html: moveOrdersMessage(ordersAlreadyInBundle, bundleDraft.id),
      title: 'Move orders to bundle?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Move orders',
      cancelButtonText: 'Cancel',
    })
      .then(async result => {
        if (result.value) {
          await removeOrdersFromBundle(
            ordersAlreadyInBundle.map(ob => ob.orderId)
          );
          // retry adding the orders to the bundle
          addSelectedOrdersToBundleDraft(
            isLoading,
            bundleDraftId,
            bundleDrafts,
            newInstructions,
            region,
            true
          );
        }
      })
      .catch(error => console.error(error.message));
  } else {
    alert('Something went wrong');
  }
}

export const cancelBundle = async (bundleId: string): Promise<boolean> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleId}/cancel`,
  });

  return response.ok;
};
